import { DatabasesExternalAssetsDisplayGridRowData } from '@tableau/data-catalog-client-components';

import { updateOrDeleteCertificationForItem } from '~src/catalog/restApi/dataQuality/setDataQualityCertificationForItem';

import { Messages } from '../../codegen/Localize';
import { IEmptyResult, IResult } from '../../httpClient/serverApi/types/IResponse';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { ILuidItemInfo } from '../../ts/models/resourceInfoParts';
import { IActionResource, IListResource } from '../../ts/resources/Resource';
import { IActionResult } from '../../ts/ServerApi/types/IActionResult';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { changeTagsOnCatalogContent } from '../CatalogTagsHelper';
import { DatabasesGraphQLResourceQuery } from '../fetchers/DatabasesGraphQLResourceQuery';
import { DeleteDatabasesRequest } from '../serverApi/requests/databases/DeleteDatabasesRequest';
import { GetDatabaseActionsRequest } from '../serverApi/requests/databases/GetDatabaseActionsRequest';
import { IDatabaseCertificationParams } from '../serverApi/requests/databases/SetDatabaseCertificationRequest';
import { DisableDatabaseControlledPermissionsRequest, EnableDatabaseControlledPermissionsRequest } from '../serverApi/requests/databases/SetDatabaseControlledPermissionsRequest';
import { IDatabaseDescriptionParams, SetDatabaseDescriptionRequest } from '../serverApi/requests/databases/SetDatabaseDescriptionRequest';
import { IDatabaseContactParams, SetDatabasesContactRequest } from '../serverApi/requests/databases/SetDatabasesContactRequest';
import { DatabaseAllowedActions } from '../serverApi/types/DatabaseAllowedActions';

class DatabasesResource implements IActionResource<DatabaseAllowedActions>, IListResource<DatabasesExternalAssetsDisplayGridRowData> {

  private allQuery = new DatabasesGraphQLResourceQuery();

  public all(): DatabasesGraphQLResourceQuery {
    return this.allQuery;
  }

  public actions(id: string): Promise<DatabaseAllowedActions> {
    return WebHttpClient.sendRequest(new GetDatabaseActionsRequest(id));
  }

  public enableDatabaseControlledPermissions(ids: string[]): Promise<IEmptyResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(
      new EnableDatabaseControlledPermissionsRequest(ids));
  }

  public disableDatabaseControlledPermissions(ids: string[]): Promise<IActionResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(
      new DisableDatabaseControlledPermissionsRequest(ids));
  }

  public deleteDatabase = async (id: string, name: string): Promise<IResult> => {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new DeleteDatabasesRequest({ ids: [id] }))
      .then(r => this.toast(r, Messages.removeExternalContentSuccessful({ ITEM_NAME: name }), Messages.removeFailed()));
  }

  public setDatabaseDescription = async (args: IDatabaseDescriptionParams): Promise<IResult> => {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetDatabaseDescriptionRequest(args))
      .then(r => this.toast(r, Messages.editDescriptionSuccessfulWithWait(), Messages.editDescriptionFailure()));
  }

  public setDatabaseCertification = async (args: IDatabaseCertificationParams): Promise<IResult> => {
    return updateOrDeleteCertificationForItem({ ...args, contentType: 'database' })
      .then(r => this.toast(r, Messages.editCertificationSuccessful(), Messages.editCertificationFailure()));
  }

  public setContact = async (args: IDatabaseContactParams): Promise<IResult> => {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetDatabasesContactRequest(args));
  }

  public async changeTags(params: {
    databases: ReadonlyArray<ILuidItemInfo>;
    tagsToAdd: ReadonlyArray<string>;
    tagsToRemove: ReadonlyArray<string>;
  }): Promise<void> {
    const { databases, tagsToAdd, tagsToRemove } = params;
    const luids = databases.map(d => d.luid);
    return changeTagsOnCatalogContent({ luids, contentType: 'database', tagsToAdd, tagsToRemove });
  }

  private toast(result: IResult, successMessage: string, failureMessage: string): any {
    if (result.errors && result.errors.length > 0) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: failureMessage
      });
    } else {
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: successMessage
      });
    }
  }
}

export const Databases = new DatabasesResource();
export type Databases = typeof Databases;
